/**
 * API Client Configuration
 * NOT auto-generated. Configures the generated `client` singleton so that
 * auth settings survive regeneration (npm run generate:api / npm run gen:prod).
 *
 * Import once in main.tsx as a side-effect:
 *   import '@/lib/api'
 */

import { client } from '@/services/client/client.gen'

// ─── 1. Base URL + credentials ───────────────────────────────────────────────
// `credentials: 'include'` ensures the HTTP-only refresh token cookie is sent
client.setConfig({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  credentials: 'include',
})

// ─── 2. Request interceptor: inject Bearer token from auth store ──────────────
client.interceptors.request.use(async (request) => {
  // Dynamic import avoids a circular dependency: auth.store → api → auth.store
  const { useAuthStore } = await import('@/features/auth/auth.store')
  const token = useAuthStore.getState().accessToken
  if (token) {
    // Request headers are immutable — create a new Request with the added header
    const headers = new Headers(request.headers)
    headers.set('Authorization', `Bearer ${token}`)
    return new Request(request, { headers })
  }
  return request
})

// ─── 3. Response interceptor: 401 → refresh token → retry once ───────────────
let isRefreshing = false

client.interceptors.response.use(async (response, request) => {
  if (response.status !== 401) return response

  // Never retry the refresh or login endpoints (prevents infinite loop)
  if (request.url.includes('/auth/refresh') || request.url.includes('/auth/login')) {
    return response
  }

  // If a refresh is already in-flight, return the 401 without stacking retries
  if (isRefreshing) return response

  isRefreshing = true

  try {
    const { authApi } = await import('@/features/auth/auth.api')
    const { useAuthStore } = await import('@/features/auth/auth.store')

    const refreshResult = await authApi.refreshToken()
    useAuthStore.getState().login(refreshResult.accessToken, refreshResult.user)

    // Retry the original request with the new token
    const headers = new Headers(request.headers)
    headers.set('Authorization', `Bearer ${refreshResult.accessToken}`)
    const retryRequest = new Request(request, { headers })
    return await fetch(retryRequest)
  } catch {
    // Refresh failed — clear auth state and surface the original 401
    const { useAuthStore } = await import('@/features/auth/auth.store')
    useAuthStore.getState().logout()
    return response
  } finally {
    isRefreshing = false
  }
})
